/**
 * shared/event-type-labels.ts
 *
 * The curated event-type → plain-language label map, moved from
 * `client/src/lib/terminology.ts` (module 01 Task 5, spec §4, AC-6) so the
 * server search layer (`server/logic/audit/query-builder.ts`) can also
 * substring-match against label VALUES without duplicating the map.
 * `terminology.ts` re-exports this verbatim so any other direct importer of
 * its `EVENT_TYPE_LABELS` keeps working; `eventTypeLabel` is unchanged — it
 * already references `EVENT_TYPE_LABELS` by the same identifier, now sourced
 * from this import.
 *
 * The raw token must NEVER appear in the client output verbatim for an
 * unlisted key — unknown tokens are humanized client-side via `safePlainLabel`.
 */

export const EVENT_TYPE_LABELS: Record<string, string> = {
  // Identity
  PROFILE_CREATED: "Account created",
  WEBAUTHN_REGISTERED: "Sign-in method added",
  WEBAUTHN_AUTHENTICATED: "Signed in",
  AAL_UPGRADED: "Stronger verification confirmed",
  AAL_DOWNGRADED: "Verification level reduced",

  // Org
  ORG_CREATED: "Organisation created",
  ORG_INVITE_SENT: "Invite sent",
  ORG_LINK_APPROVED: "Organisation link approved",
  ORG_LINK_REJECTED: "Organisation link rejected",
  ORG_LINK_SEVERED: "Organisation link removed",
  ORG_APPROVED: "Organisation approved",
  ORG_SUSPENDED: "Organisation suspended",
  ORG_MEMBER_ADDED: "Member added",
  ORG_MEMBER_REMOVED: "Member removed",

  // Verification
  CHALLENGE_INITIATED: "Verification started",
  CHALLENGE_COMPLETED: "Challenge completed",
  CHALLENGE_FAILED: "Verification failed",
  INTERVIEW_STARTED: "Memory interview started",
  INTERVIEW_COMPLETED: "Memory interview completed",
  INTERVIEW_EXPIRED: "Memory interview expired",
  CHALLENGE_COOLDOWN_ENFORCED: "Verification paused — too many attempts",
  CHALLENGE_GUARDIAN_ALERT: "Guardians alerted",
  ONBOARDING_COMPLETED: "Setup completed",
  AUDIT_CERTIFICATE_ISSUED: "Audit record issued",

  // Vault
  VAULT_SEED_CREATED: "Memory added",
  VAULT_SEED_VERIFIED: "Memory verified",
  VAULT_ARTIFACT_STORED: "Saved a vault item",
  VAULT_ARTIFACT_SHARED: "Shared a vault item",

  // Guardian
  GUARDIAN_CONNECTED: "Guardian connected",
  GUARDIAN_QUORUM_VOTE: "Guardian voted",
  GUARDIAN_REVIEW_COMPLETED: "Guardian review done",

  // Safety
  DURESS_PATH1_ACTIVATED: "Duress signal sent",
  DURESS_PATH2_PAUSE: "Account paused for safety",
  FORENSIC_PAUSE_ACTIVATED: "Account locked — security alert",
  FORENSIC_PAUSE_LIFTED: "Account unlocked",
  SAFE_RELEASE_COMPLETED: "Safe release completed",
  DURESS_PATH2_ACTIVATED: "Duress protection activated",
  DURESS_RELEASED: "Duress mode ended",
  DURESS_RELEASE_CANCELLED: "Safe release cancelled",

  // Device
  DEVICE_TRANSFER_INITIATED: "Device transfer started",
  DEVICE_TRANSFER_COMPLETED: "Device transfer completed",
  ACCOUNT_RECOVERY_INITIATED: "Account recovery started",
  ACCOUNT_RECOVERY_COMPLETED: "Account recovery completed",

  // System
  SESSION_STARTED: "Session started",
  SESSION_TERMINATED: "Session ended",
  FEATURE_FLAG_CHANGED: "Feature setting changed",
  ANOMALY_DETECTED: "Unusual activity detected",
  RATE_LIMIT_ENFORCED: "Too many requests — temporarily paused",
  ROLLBACK_EXECUTED: "Change reversed",

  // Vault Identifiers
  VAULT_IDENTIFIER_CREATED: "Identity item added",
  VAULT_IDENTIFIER_UPDATED: "Identity item updated",
  VAULT_IDENTIFIER_DELETED: "Identity item removed",
  VAULT_IDENTIFIER_VERIFICATION_INITIATED: "Identity item verification started",
  VAULT_IDENTIFIER_VERIFICATION_COMPLETED: "Identity item verified",
  VAULT_IDENTIFIER_VERIFICATION_FAILED: "Identity item verification failed",
  VAULT_IDENTIFIER_TIER_UPGRADED: "Identity item trust level raised",
  VAULT_IDENTIFIER_OWNERSHIP_CONFIRMED: "Ownership confirmed",
  VAULT_IDENTIFIER_USAGE_LIMIT_HIT: "Identity item limit reached",
  VAULT_IDENTIFIER_STALE_DELETED: "Pending identity item removed",
  VAULT_IDENTIFIER_ORG_USED: "Identity item used by organisation",
  VAULT_IDENTIFIER_SUSPENDED: "Identity item suspended",
  IDENTIFIER_USAGE_LIMIT_CHANGED: "Identity item limit updated",

  // Guardian Ceremonies
  GUARDIAN_PAIRING_INITIATED: "Guardian pairing started",
  GUARDIAN_PAIRING_JOINED: "Guardian pairing joined",
  GUARDIAN_PAIRING_CODE_CONFIRMED: "Pairing code confirmed",
  GUARDIAN_PAIRING_COMPLETED: "Guardian pairing completed",
  GUARDIAN_PAIRING_EXPIRED: "Guardian pairing expired",
  GUARDIAN_INVITE_CREATED: "Guardian invite created",
  GUARDIAN_INVITE_OPENED: "Guardian invite opened",
  GUARDIAN_INVITE_REJECTED: "Guardian invite rejected",
  GUARDIAN_INVITE_ATTEMPTS_EXHAUSTED: "Guardian invite — all attempts used",
  GUARDIAN_INVITE_DISPUTED: "Guardian invite disputed",
  GUARDIAN_INVITE_EXPIRED: "Guardian invite expired",
  GUARDIAN_QUESTIONS_CREATED: "Verification questions set",
  GUARDIAN_QUESTIONS_ANSWERED: "Verification questions answered",
  GUARDIAN_QUESTIONS_PASSED: "Verification questions passed",
  GUARDIAN_QUESTIONS_FAILED: "Verification questions failed",
  GUARDIAN_CONNECTION_ESTABLISHED: "Guardian connection established",
  GUARDIAN_RECIPROCAL_OFFERED: "Mutual guardian offer sent",
  GUARDIAN_RECIPROCAL_ACCEPTED: "Mutual guardian offer accepted",
  GUARDIAN_RECIPROCAL_DECLINED: "Mutual guardian offer declined",
  GUARDIAN_BIDIRECTIONAL_LINKED: "Mutual guardian link created",
  GUARDIAN_MGMT_SESSION_STARTED: "Guardian management started",
  GUARDIAN_MGMT_SESSION_EXPIRED: "Guardian management session expired",
  GUARDIAN_MGMT_SESSION_ENDED: "Guardian management ended",
  GUARDIAN_INVITE_RATE_LIMITED: "Guardian invite paused — too many attempts",
  GUARDIAN_MAX_REACHED: "Guardian limit reached",
  GUARDIAN_FOLLOWUP_ASKED: "Follow-up question asked",
  GUARDIAN_FOLLOWUP_ANSWERED: "Follow-up question answered",
  GUARDIAN_LOCATION_SHARING_ENABLED: "Location sharing enabled",
  GUARDIAN_LOCATION_SHARING_DISABLED: "Location sharing disabled",
  GUARDIAN_REMOVAL_COOLING_STARTED: "Guardian removal started",
  GUARDIAN_REMOVAL_COOLING_CANCELLED: "Guardian removal cancelled",
  GUARDIAN_REMOVAL_COOLING_COMPLETED: "Guardian removed",
  GUARDIAN_REMOVAL_QUORUM_REQUIRED: "Guardian removal needs approval",
  GUARDIAN_REMOVAL_QUORUM_APPROVED: "Guardian removal approved",
  GUARDIAN_REMOVAL_BLOCKED: "Guardian removal blocked",
  GUARDIAN_RAPID_CHANGES_DETECTED: "Rapid guardian changes detected",

  // Step-Up
  STEPUP_INITIATED: "Protected action started",
  STEPUP_OWN_SEEDS_PASSED: "Memory check passed",
  STEPUP_OWN_SEEDS_FAILED: "Memory check failed",
  STEPUP_GUARDIAN_QUESTIONS_PASSED: "Shared questions passed",
  STEPUP_GUARDIAN_QUESTIONS_FAILED: "Shared questions failed",
  STEPUP_REQUEST_SENT: "Guardian approval requested",
  STEPUP_GUARDIAN_APPROVED: "Guardian approved action",
  STEPUP_GUARDIAN_REJECTED: "Guardian rejected action",
  STEPUP_COMPLETED: "Protected action approved",
  STEPUP_DENIED: "Protected action denied",
  STEPUP_REQUEST_RECEIVED: "Approval request received",
  STEPUP_GUARDIAN_OWN_QUESTIONS_PASSED: "Guardian memory check passed",
  STEPUP_GUARDIAN_OWN_QUESTIONS_FAILED: "Guardian memory check failed",
  STEPUP_GUARDIAN_RESPONDED: "Guardian responded to request",
  STEPUP_COOLDOWN_ENFORCED: "Approval paused — too many attempts",
  STEPUP_EXPIRED: "Approval request expired",

  // Guardian Review Cycle
  GUARDIAN_REVIEW_PROMPTED: "Guardian review reminder sent",
  GUARDIAN_REVIEW_DISMISSED: "Guardian review dismissed",
  GUARDIAN_REVIEW_OVERDUE: "Guardian review overdue",
  GUARDIAN_REMOVED_DURING_REVIEW: "Guardian removed during review",
  GUARDIAN_REPLACEMENT_INITIATED: "Guardian replacement started",
  GUARDIAN_CIRCLE_DEGRADED: "Guardian circle weakened",
  GUARDIAN_CIRCLE_RESTORED: "Guardian circle restored",
  GUARDIAN_OPERATION_BLOCKED: "Action blocked — not enough guardians",

  // Anomaly
  ANOMALY_CHALLENGE_PASSED: "Unusual-activity check passed",
  ANOMALY_CHALLENGE_FAILED: "Unusual-activity check failed",
  ANOMALY_CHALLENGE_TIMEOUT: "Unusual-activity check timed out",
  ANOMALY_STEPUP_PASSED: "Guardian safety check passed",
  ANOMALY_STEPUP_FAILED: "Guardian safety check failed",

  // Org Actions
  ORG_CONNECTION_SUSPENDED: "Organisation connection suspended",
  ORG_PENDING_CANCELLED: "Pending actions cancelled",
  ORG_CONCERN_FLAGGED: "Concern flagged",
  ORG_CONNECTION_RESTORED: "Organisation connection restored",

  // System (continued)
  STREM_ADMIN_INTERVENTION: "Admin action taken",

  // External API — Access
  API_ACCESS_REQUESTED: "API access requested",
  API_ACCESS_APPROVED: "API access approved",
  API_ACCESS_REVOKED: "API access revoked",
  API_ACCESS_SUSPENDED: "API access suspended",

  // External API — Domain Verification
  ORG_DOMAIN_VERIFICATION_INITIATED: "Domain verification started",
  ORG_DOMAIN_VERIFICATION_CONFIRMED: "Domain verified",
  ORG_DOMAIN_VERIFICATION_FAILED: "Domain verification failed",

  // External API — Certificates
  API_CERT_ISSUED: "API certificate issued",
  API_CERT_ROTATED: "API certificate rotated",
  API_CERT_REVOKED: "API certificate revoked",
  API_CERT_EXPIRING: "API certificate expiring soon",

  // External API — Keys
  API_KEY_CREATED: "API key created",
  API_KEY_REVOKED: "API key revoked",

  // External API — Verification Broker
  API_VERIFICATION_REQUESTED: "Verification requested",
  API_VERIFICATION_COMPLETED: "Verification completed",
  API_VERIFICATION_FAILED: "Verification failed",
  API_VERIFICATION_EXPIRED: "Verification request expired",
  API_VERIFICATION_CANCELLED: "Verification cancelled",

  // External API — Webhooks
  API_WEBHOOK_REGISTERED: "Notification endpoint added",
  API_WEBHOOK_VERIFIED: "Notification endpoint verified",
  API_WEBHOOK_UPDATED: "Notification endpoint updated",
  API_WEBHOOK_DELETED: "Notification endpoint removed",
  API_WEBHOOK_DISABLED: "Notification endpoint disabled",
  API_WEBHOOK_DELIVERY_FAILED: "Notification delivery failed",

  // External API — Rate Limiting
  API_RATE_CONFIG_UPDATED: "API rate limits updated",
  API_RATE_LIMIT_EXCEEDED: "API rate limit reached",

  // Anomaly detection — cancellation ceremony
  "anomaly.cancellation.initiated": "Cancellation started",
  "anomaly.cancellation.confirmed": "Cancellation confirmed",
  "anomaly.cancellation.expired": "Cancellation expired",

  // Anomaly detection — alert lifecycle
  "anomaly.alert.escalated": "Alert escalated",
  "anomaly.alert.dismissed": "Alert dismissed",
  "anomaly.alert.auto_resolved": "Alert resolved automatically",
  // Anomaly detection — cross-org / sign-in downward-force evaluation itself failed
  "anomaly.cross_org.flagged": "Cross-organisation concern flagged",
  "anomaly.sign_in_evaluation_failed": "Sign-in anomaly check failed",

  // Challenge resolution lifecycle
  "challenge.issue":   "Confirmation request sent",
  "challenge.resolve": "Challenge resolved",
  "challenge.cancel":  "Challenge cancelled",
  // Bank
  "bank.reveal": "Bank details revealed",

  // Retrospective flags
  "retrospective_flag":          "Retrospective flag raised",
  "retrospective_flag.response": "Retrospective flag answered",

  // Vault lifecycle (dotted variants)
  "vault.reseeding.required":  "Vault re-seed required",
  "vault.seed.osint_overlap":  "Public-data overlap found",

  // Security anomalies (dotted variants)
  "webauthn.counter_anomaly":    "Security key anomaly detected",
  // NOTE: "acoustic.injection_attack" mapping retired (voice-verification-
  // removal AC-2) — an old raw token now falls back to the generic "Event"
  // label via safePlainLabel/JARGON_PATTERN (which still bans "acoustic" so
  // the internal term can never leak verbatim on screen).

  // Sign-in method lifecycle (dotted; no parallel SCREAMING_SNAKE key)
  "webauthn.credential.registered": "Sign-in method added",

  // Org member lifecycle (dotted; distinct from the legacy SCREAMING_SNAKE
  // ORG_MEMBER_ADDED/ORG_MEMBER_REMOVED tokens above)
  "org.member.joined": "Org member joined",
  "org.member.invited": "Team member invited",
  "org.member.invite_revoked": "Team member invite revoked",
  "org.member.removed": "Org member removed",
  "org.member.role_changed": "Org member role changed",

  // Org membership / admin-grant / policy / connections (phrasing mirrors
  // server forensic-summary.ts verbatim)
  "org.membership.requested": "Team membership requested",
  "org.admin_grant.requested": "Administrator grant requested",
  "org.admin_grant.signed": "Administrator grant signed",
  "org.admin_grant.approved": "Administrator grant approved",
  "org.admin_grant.declined": "Administrator grant declined",
  "platform.operator.granted": "Platform operator granted",
  "platform.operator.revoked": "Platform operator revoked",
  "org.channel_policy.updated": "Channel policy updated",
  "org_connection_org-to-org_accepted": "Organisation connection accepted",
  "org_connection_user-to-org_accepted": "Individual connection accepted",
  "individual.connection.requested": "Individual connection requested",

  "comm.stamp": "Message stamped",
  "comm.stamp.window": "Message stamp (5-minute window)",
  "comm.stamp.batch": "Message batch stamp",
  "deal.created": "Deal created",
  "deal.archived": "Deal archived",
  "deal.restored": "Deal restored",
  "deal.cloned": "Deal cloned",
  "deal.participant_added": "Deal participant added",
  "deal.payment_in.scheduled": "Deal payment in scheduled",
  "deal.payment_out.scheduled": "Deal payment out scheduled",
  "deal.payment_in.notified": "Deal payment in notice sent",
  "deal.import_committed": "Deal CSV import committed",
  "aba_export.attested": "Payout file approved",
  "aba_export.approval": "Payout file approval signed",
  "id_verification.identity_mismatch": "Identity detail mismatch",
  "bespoke.seed.batch_created": "Bespoke memory batch banked",
  "challenge.workflow.cancel": "Approval workflow cancelled",

  // Generic audit record
  "audit": "Audit record",
};
